import { CarOutlined, IdcardOutlined, QrcodeOutlined } from '@ant-design/icons'

import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import RadioOption from '../../components/Booking/RadioOption'
import SlideCard from '../../components/Booking/SlideCard'
import PaymentReceiptComponent from '../../components/PaymentReceiptComponent'
import config from '../../config'
import {
  IAuthContext,
  IResultQueryReservationJoinOnUserAndChargeAndVehicle,
  UserModel,
} from '../../services/BookingChecking/bookingCheckin.interface'
import {
  genUserImageUrl,
  getOmiselinkCheckin,
  getReservationInfo,
  ProcessOmiseTokenResponse,
  processOmiseTokenWithPayment,
} from '../../services/BookingChecking/bookingCheckin.service'
import omiseService, { OmiseCardData } from '../../services/Omise/Omise.service'
import driverCardLogo from '../../static/img/icon/booking/Driver.svg'
import passportLogo from '../../static/img/icon/booking/Passport.svg'
import alipay from '../../static/img/icon/payment/Alipay.svg'
import master from '../../static/img/icon/payment/MasterCard.svg'
import visa from '../../static/img/icon/payment/Visa.svg'
import { downloadReceiptAsImage, getSuccessfulCharge } from '../../utils/receiptGenerator'
import { VehiclePicture } from './reservation.interface'
import { BookingCheckinUploadItem } from './uploadComponent'

// Card form data interface
interface CardFormValues {
  cardName: string
  cardNumber: string
  expiryMonth: string
  expiryYear: string
  cvv: string
}

const { Text } = Typography

interface IradioOption {
  value: number
  label: string
  imgUrl: string[]
}

// Style definitions
const styleIcon: React.CSSProperties = {
  fontSize: '20px',
  color: '#fff',
  marginLeft: '1rem',
}

const BookingCheckin: React.FC = () => {
  const location = useLocation()
  const history = useHistory()

  // Extract token from URL query parameters
  const [token, setToken] = useState<string | null>(null)

  // UI State
  const [radioV, setRadioV] = useState(1)
  const [matches, setMatches] = useState(window.matchMedia('(min-width: 768px)').matches)

  // Reservation info state
  const [reservationInfo, setReservationInfo] =
    useState<IResultQueryReservationJoinOnUserAndChargeAndVehicle | null>(null)
  const [loading, setLoading] = useState(false)

  // Payment state
  const [loadingPayment, setLoadingPayment] = useState(false)
  const [chargeSuccess, setChargeSuccess] = useState(false)
  const [fetchFailureCount, setFetchFailureCount] = useState(0)

  // Waiting for payment state
  const [waitingForPayment, setWaitingForPayment] = useState(false)

  // Card form state for Credit/Debit card payments
  const [cardForm] = Form.useForm()
  const [showCardForm, setShowCardForm] = useState(true)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [paymentStep, setPaymentStep] = useState<'processing' | 'success' | 'error'>('processing')
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string>('')

  // Payment object based on the service interface
  const payment = {
    chargemethod: 'OMISELINK',
    genlink: true,
    genlink_promptpay: false,
  }

  // Document images state to store the actual image URLs
  const [documentImages, setDocumentImages] = useState<{
    cl_front?: string // passport image URL
    cl_rear?: string // selfie with passport image URL
    dl_front?: string // driver license image URL
    dl_rear?: string // selfie with driver license image URL
  }>({})

  // Document type mapping for display names
  const DOCUMENT_MAPPING = {
    cl_front: 'Passport',
    cl_rear: 'Selfie with Passport',
    dl_front: 'Driver License',
    dl_rear: 'Selfie with Driver License',
  }

  // Listen for payment status changes
  const listenPayment = async () => {
    try {
      if (!token) return

      const result = await getReservationInfo(token)
      if (result.result) {
        const resInfo = result.result.reservationData
        // Get the last object from chargeData array
        const chargeData = resInfo?.chargeData
        const charge_last =
          chargeData && chargeData.length > 0
            ? chargeData[chargeData.length - 1]?.chargestate
            : null

        if (charge_last) {
          if (charge_last.includes('CHARGED')) {
            setChargeSuccess(true)
            setWaitingForPayment(false) // Clear waiting state when payment is successful
            // Update the reservation info with the latest data
            setReservationInfo(result.result)
          }
        }

        // Reset failure count on successful fetch
        setFetchFailureCount(0)
      } else {
        // Increment failure count on unsuccessful result
        const newFailureCount = fetchFailureCount + 1
        setFetchFailureCount(newFailureCount)

        if (newFailureCount >= 2) {
          message.error('Failed to fetch payment status multiple times. Redirecting to home page.')
          setTimeout(() => history.push('/'), 1000)
        }
      }
    } catch (error: unknown) {
      // Increment failure count on error
      const newFailureCount = fetchFailureCount + 1
      setFetchFailureCount(newFailureCount)

      if (newFailureCount >= 2) {
        message.error('Failed to fetch payment status multiple times. Redirecting to home page.')
        setTimeout(() => history.push('/'), 1000)
      } else {
        message.error(`Message Error: ${error}`)
      }
    }
  }

  // Extract token from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const tokenParam = urlParams.get('token')
    setToken(tokenParam)
  }, [location.search])

  // Vehicle pictures state
  const [vehiclePictures, setVehiclePictures] = useState<VehiclePicture[]>([])

  // Function to refresh document images (call this after successful upload)
  const refreshDocumentImages = async () => {
    if (reservationInfo?.userData && token) {
      await fetchDocumentImageUrls(reservationInfo.userData, token)
    }
  }

  // Function to fetch document image URLs from the API
  const fetchDocumentImageUrls = async (userData: UserModel, userToken: string) => {
    const auth: IAuthContext = {
      user: { id: userData.userid, token: userToken },
      userLoggedIn: { userToken },
    }

    const imageUrls: { [key: string]: string } = {}

    // Check each document type and fetch image URL if the file path exists
    const documentTypes = ['cl_front', 'cl_rear', 'dl_front', 'dl_rear'] as const

    for (const docType of documentTypes) {
      const pathfile = userData[docType]
      if (pathfile) {
        try {
          const response = await genUserImageUrl(auth, {
            userid: userData.userid,
            pathfile,
          })

          if (response?.imageUrl) {
            // Combine base URL with imageUrl from response, or use imageUrl directly if base URL is undefined
            const baseImageUrl = config.webserverService.URL
              ? `${config.webserverService.URL}${response.imageUrl}`
              : `${response.imageUrl}`

            imageUrls[docType] = baseImageUrl
          }
        } catch (error) {
          // Failed to fetch image URL
        }
      }
    }

    setDocumentImages(imageUrls)
  }

  // Fetch reservation info when token is available
  useEffect(() => {
    if (!token) return
    setLoading(true)
    getReservationInfo(token)
      .then((result) => {
        if (result.result) {
          setReservationInfo(result.result)

          // Check charge status immediately on initial load
          const resInfo = result.result.reservationData
          const chargeData = resInfo?.chargeData
          const charge_last =
            chargeData && chargeData.length > 0
              ? chargeData[chargeData.length - 1]?.chargestate
              : null

          if (charge_last && charge_last.includes('CHARGED')) {
            setChargeSuccess(true)
          }

          // Fetch document image URLs if userData has document file paths
          const userData = result.result.userData
          if (userData && token) {
            fetchDocumentImageUrls(userData, token)
          }

          // Set vehicle pictures if available
          if (result.result.vehiclePicture && result.result.vehiclePicture.length > 0) {
            // Convert VehiclePictureAttributes to VehiclePicture format
            const convertedPictures: VehiclePicture[] = result.result.vehiclePicture.map((pic) => ({
              pictureno: pic.pictureno,
              vehicleid: pic.vehicleid,
              no: pic.no,
              logtime:
                pic.logtime instanceof Date ? pic.logtime.toISOString() : String(pic.logtime),
              url: pic.url,
              description: pic.description,
              car_side: pic.car_side || '',
              description_th: pic.description_th || '',
              description_en: pic.description_en || '',
            }))
            setVehiclePictures(convertedPictures)
          } else if (result.result.reservationData?.vehicleData?.pic_path) {
            // Fallback to single pic_path if vehiclePicture array is not available
            setVehiclePictures([
              {
                pictureno: 0,
                vehicleid: result.result.reservationData.vehicleData.vehicleid,
                no: 0,
                logtime: new Date().toISOString(),
                url: result.result.reservationData.vehicleData.pic_path,
                description: '',
                car_side: '',
                description_th: '',
                description_en: '',
              },
            ])
          }
        } else {
          message.error(result.message || 'Failed to fetch reservation info')
          // Redirect to home page if reservation info failed to fetch
          setTimeout(() => history.push('/'), 500)
        }
      })
      .catch(() => {
        message.error('Failed to fetch reservation info')
        // Redirect to home page if request failed (invalid token or network error)
        setTimeout(() => history.push('/'), 500)
      })
      .finally(() => setLoading(false))
  }, [token])

  // Clear waiting state when payment is successful
  useEffect(() => {
    if (chargeSuccess) {
      setWaitingForPayment(false)
    }
  }, [chargeSuccess])

  // Event handlers
  const handleChangeRadio = (e: RadioChangeEvent) => {
    setRadioV(e.target.value)
    setShowCardForm(e.target.value === 1) // Show card form for Credit/Debit cards (value 1)
  }

  // Function to check if there's a pending payment (only for Credit/Debit cards)
  const hasPendingPayment = () => {
    // Credit/Debit cards use form-based tokenization, not link-based payment
    if (radioV === 1) return false

    const chargeData = reservationInfo?.reservationData?.chargeData
    if (chargeData && chargeData.length > 0) {
      return chargeData.some((charge) => charge.chargestate === 'PENDING' && charge.link_url)
    }
    return false
  }

  // Function to handle retry payment (reopen existing payment link)
  const handleRetryPayment = () => {
    const chargeData = reservationInfo?.reservationData?.chargeData
    if (chargeData && chargeData.length > 0) {
      const pendingCharge = chargeData
        .slice()
        .reverse()
        .find((charge) => charge.chargestate === 'PENDING' && charge.link_url)

      if (pendingCharge && pendingCharge.link_url) {
        // Set waiting state first before any other operations
        setWaitingForPayment(true)

        message.info('Reopening payment link...')
        // Start listening for payment status
        listenPayment()

        // Open in new tab for Credit/Debit cards (radioV === 1), otherwise redirect current page
        if (radioV === 1) {
          window.open(pendingCharge.link_url, '_blank')
        } else {
          window.location.href = pendingCharge.link_url
        }
      }
    }
  }

  const handleConfirm = async () => {
    try {
      if (!token) {
        message.error('Missing authentication token')
        return
      }

      // Prevent multiple clicks while waiting for payment
      if (waitingForPayment) {
        message.warning('Payment link already created. Please complete the payment.')
        return
      }

      setLoadingPayment(true)

      // Check if user selected credit/debit card and validate form
      if (radioV === 1) {
        // Validate card form before proceeding
        try {
          const values: CardFormValues = await cardForm.validateFields()

          // Step 1: Show payment modal with processing state for credit card
          setPaymentModalVisible(true)
          setPaymentStep('processing')

          // Step 2: Create card data for Omise
          const cardData: OmiseCardData = {
            name: values.cardName,
            number: values.cardNumber.replace(/\s/g, ''), // Remove spaces
            expiration_month: parseInt(values.expiryMonth),
            expiration_year: parseInt(values.expiryYear),
            security_code: values.cvv,
          }

          // Step 3: Get token ID from Omise service
          const tokenId = await omiseService.getTokenId(cardData)

          if (!tokenId) {
            setPaymentStep('error')
            setPaymentErrorMessage(
              'Failed to create payment token. Please check your card details.',
            )
            message.error('Failed to create payment token. Please check your card details.')
            return
          }

          // Step 4: Process the Omise token with payment (with timeout)
          let paymentResult: ProcessOmiseTokenResponse
          try {
            paymentResult = await Promise.race([
              processOmiseTokenWithPayment(tokenId, token),
              new Promise<ProcessOmiseTokenResponse>((_, reject) =>
                setTimeout(
                  () => reject(new Error('Payment request timed out. Please try again.')),
                  120000,
                ),
              ),
            ])
          } catch (timeoutError) {
            setPaymentStep('error')
            setPaymentErrorMessage(
              timeoutError instanceof Error ? timeoutError.message : 'Payment request timed out.',
            )
            message.error(
              timeoutError instanceof Error ? timeoutError.message : 'Payment request timed out.',
            )
            return
          }

          if (paymentResult.result) {
            // Step 5: Payment successful
            setPaymentStep('success')

            // Step 6: Refetch reservation info after successful payment
            setTimeout(async () => {
              try {
                listenPayment()
              } catch (error) {
                // Error handling for refetch
              }

              // Close modal after a short delay to show success
              setTimeout(() => {
                setPaymentModalVisible(false)
                setPaymentStep('processing')
                setPaymentErrorMessage('') // Clear error message
              }, 2000)
            }, 1000)
            return // Exit early for successful credit card payment
          } else {
            // Payment failed - show error message from API
            const errorMessage = paymentResult.message || 'Payment processing failed'
            setPaymentStep('error')
            setPaymentErrorMessage(errorMessage)
            message.error(errorMessage)
            return
          }
        } catch (cardError: unknown) {
          // Card form validation failed or card processing error
          if (cardError instanceof Error && cardError.message.includes('validation')) {
            message.error('Please fill in all required card information')
          } else {
            const errorMessage =
              cardError instanceof Error
                ? cardError.message
                : 'An unexpected error occurred during payment processing'
            setPaymentStep('error')
            setPaymentErrorMessage(errorMessage)
            message.error(errorMessage)
          }
          return
        }
      }

      // For Alipay (radioV === 2) or fallback payment link creation
      // Check if there's already a pending charge in chargeData array
      // Only for Credit/Debit cards (radioV === 1), for other payment methods create new link
      const chargeData = reservationInfo?.reservationData?.chargeData
      let existingPendingCharge = null

      if (radioV === 1 && chargeData && chargeData.length > 0) {
        // Find the most recent charge with PENDING status only for Credit/Debit cards
        existingPendingCharge = chargeData
          .slice()
          .reverse()
          .find((charge) => charge.chargestate === 'PENDING' && charge.link_url)
      }

      let linkUrl = null

      if (existingPendingCharge) {
        // Use existing pending charge link (only for Credit/Debit cards)
        linkUrl = existingPendingCharge.link_url
        message.info('Payment is processing. Opening existing payment link...')
      } else {
        // Create new payment link (always for Alipay, or new Credit/Debit payment)
        message.info('Payment is processing. Creating new payment link...')
        let response
        if (radioV == 1) {
          response = await getOmiselinkCheckin({ ...payment, channel: '' }, token)
        } else {
          response = await getOmiselinkCheckin({ ...payment, channel: 'ALIPAY' }, token)
        }

        setChargeSuccess(false)

        // Extract link_url from the response based on the updated interface
        // The link_url is now nested under the charge property
        linkUrl = response?.charge?.link_url || null

        // Set waiting for payment state when new payment link is created
        if (linkUrl) {
          setWaitingForPayment(true)
        }
      }

      if (linkUrl) {
        // Start listening for payment status before redirecting
        listenPayment()

        // Open in new tab for Credit/Debit cards (radioV === 1), otherwise redirect current page
        if (radioV === 1) {
          window.open(linkUrl, '_blank')
        } else {
          window.location.href = linkUrl
        }
      } else {
        message.error('Payment link not found in response')
      }
    } catch (error: unknown) {
      message.error(`Payment Error: ${error}`)
    } finally {
      setLoadingPayment(false)
    }
  }

  // Convert time to user-friendly format
  const convertTime = (timeString: string) => {
    const date = new Date(timeString)
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }
    return date.toLocaleDateString('en-GB', options)
  }

  // Function to download receipt
  const downloadReceipt = () => {
    const charge = getSuccessfulCharge(reservationInfo)
    if (!charge || !reservationInfo) return
    downloadReceiptAsImage(reservationInfo, charge)
  }

  // Payment options
  const option: IradioOption[] = [
    {
      value: 1,
      label: 'Credit / Debit cards',
      imgUrl: [visa, master],
    },
    // {
    //   value: 2,
    //   label: 'Alipay',
    //   imgUrl: [alipay],
    // },
  ]

  // Screen size detection
  useEffect(() => {
    window.matchMedia('(min-width: 768px)').addEventListener('change', (e) => setMatches(e.matches))
  }, [])

  // Helper function to check if deposit is free
  const isDepositFree = () => {
    return reservationInfo?.tripOrderId?.depositmode === 'DEPOSIT-FREE'
  }

  // Returns the exact total the user will be charged: charge1 (base) + charge2 (3% fee)
  // Uses the same formula as the backend so the displayed number always matches what's billed
  const getChargeableDeposit = () => {
    const base =
      (reservationInfo?.reservationData?.overide_deposit !== -1
        ? reservationInfo?.reservationData?.overide_deposit
        : reservationInfo?.reservationData?.depositprice_b) || 0
    const paymentFee = Math.round(base * 0.03 * 100) / 100
    return base + paymentFee
  }

  // Helper function to get user display name and label
  const getUserDisplayInfo = () => {
    const userData = reservationInfo?.userData
    const hasName = userData?.name || userData?.lastname
    const hasEmail = userData?.email

    if (hasName) {
      return {
        label: 'Name',
        value: `${userData?.name ?? ''} ${userData?.lastname ?? ''}`.trim(),
      }
    } else if (hasEmail) {
      return {
        label: 'Email',
        value: userData.email,
      }
    } else {
      return {
        label: 'Name',
        value: 'No name or email available',
      }
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#0048B3',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: matches ? 'flex-start' : 'center',
        // padding: matches ? '2rem 0' : '0',
      }}
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            color: '#fff',
          }}
        >
          <Spin size="large" />
          <Text style={{ color: '#fff', marginTop: '1rem', fontSize: '16px' }}>
            Loading reservation information...
          </Text>
        </div>
      ) : (
        <div
          style={{
            width: matches ? '100%' : '100vw',
            maxWidth: matches ? '1200px' : '100vw',
            margin: '0 auto',
          }}
        >
          {/* Header Status Bar */}
          <div
            style={{
              marginTop: matches ? '1rem' : 0,
              width: matches ? '100%' : '100%',
              height: '43px',
              backgroundColor: '#1D2530',
              display: 'flex',
              alignItems: 'center',
              justifyContent: matches ? 'start' : 'space-between',
              borderRadius: matches ? '8px 8px 0 0' : '0',
            }}
          ></div>

          {/* Main Content Card */}
          <div
            style={{
              width: matches ? '100%' : '100%',
              backgroundColor: '#003C92',
              marginTop: matches ? '0' : 0,
              gap: '1rem',
              borderRadius: matches ? '0 0 12px 12px' : 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingBottom: matches ? '2rem' : '1rem',
              marginBottom:
                ((reservationInfo?.reservationData?.overide_deposit !== -1
                  ? reservationInfo?.reservationData?.overide_deposit
                  : reservationInfo?.reservationData?.depositprice_b) || 0) == 0
                  ? '2rem'
                  : '0',
              boxShadow: matches ? '0 4px 20px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                width: '100%',
                maxWidth: 1200,
                marginTop: '1rem',
                padding: '0 1rem',
                justifyContent: 'center',
                borderBottom: matches ? '1px solid rgba(255,255,255,0.2)' : 0,
                boxSizing: 'border-box',
              }}
            >
              <Row
                gutter={[16, 16]}
                wrap
                style={{
                  fontSize: matches ? 18 : 18,
                  color: '#fff',
                  marginBottom: matches ? '1.5rem' : '1rem',
                  fontWeight: 500,
                  background: matches ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.12)',
                  borderRadius: 8,
                  width: '100%',
                  padding: matches ? '1rem 2rem' : '1rem',
                  alignItems: 'center',
                  display: 'flex',
                  boxSizing: 'border-box',
                  justifyContent: 'center',
                }}
              >
                {/* Reservation No */}
                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  style={{
                    textAlign: 'center',
                    columnGap: 8,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0,
                    marginBottom: 0,
                  }}
                >
                  <div style={{ fontSize: matches ? 'inherit' : '20px' }}>Reservation No</div>
                  <div
                    style={{
                      fontWeight: 400,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      marginTop: 4,
                      fontSize: matches ? 'inherit' : '16px',
                      color: !reservationInfo?.reservationData?.reservationno ? '#bbb' : undefined,
                    }}
                  >
                    {reservationInfo?.reservationData?.reservationno ?? 'Loading...'}
                  </div>
                </Col>

                {/* Name - Only show on desktop */}
                {matches && (
                  <Col
                    md={8}
                    style={{
                      textAlign: 'center',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'stretch', // let children take full width
                      gap: 0,
                      marginBottom: 0,
                      maxWidth: '100%',
                      minWidth: 0, // critical in flex layouts
                    }}
                  >
                    <div>{getUserDisplayInfo().label}</div>
                    <div
                      style={{
                        fontWeight: 400,
                        marginTop: 4,
                      }}
                    >
                      {getUserDisplayInfo().value || (
                        <span style={{ color: '#bbb' }}>Loading...</span>
                      )}
                    </div>
                  </Col>
                )}

                {/* Vehicle Model - Only show on desktop */}
                {matches && (
                  <Col
                    md={8}
                    style={{
                      textAlign: 'center',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 0,
                      marginBottom: 0,
                    }}
                  >
                    <div>Vehicle Model</div>
                    <div
                      style={{
                        fontWeight: 400,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        marginTop: 4,
                      }}
                    >
                      {reservationInfo?.reservationData?.vehicleData
                        ? `${reservationInfo.reservationData.vehicleData.vehiclebrand} ${reservationInfo.reservationData.vehicleData.vehiclemodel}`
                        : 'Loading vehicle information...'}
                    </div>
                  </Col>
                )}
              </Row>
            </div>

            {/* Vehicle Image Slider */}
            {vehiclePictures.length > 0 ? (
              <SlideCard matches={matches} img={vehiclePictures} />
            ) : (
              <div
                style={{
                  width: matches ? 200 : 150,
                  height: matches ? 150 : 112.5,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '1rem 0',
                }}
              >
                <Text style={{ color: '#fff' }}>No images available</Text>
              </div>
            )}

            {/* Reservation Details */}
            <div
              style={{
                width: matches ? '90%' : '95%',
                backgroundColor: 'rgba(29, 37, 48, 0.3)',
                margin: matches ? '1rem 0 2rem 0' : '1rem 0 1rem 0',
                padding: matches ? '2rem' : '1rem 2rem 1rem 2rem',
                borderRadius: matches ? '12px' : '0',
                boxShadow: matches ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              {/* Mobile: Name and Vehicle Info Section */}
              {(!matches || reservationInfo?.tripOrderId?.trip_order_id) && (
                <Row
                  style={{
                    color: '#fff',
                    fontSize: 14,
                    width: '100%',
                    margin: '1rem 0',
                    padding: '1rem',
                    backgroundColor: 'rgba(0, 55, 134, 0.8)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {!matches && (
                    <>
                      <Col span={12} style={{ paddingRight: '8px', minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#b0c4de', marginBottom: '4px' }}>
                          {getUserDisplayInfo().label}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            lineHeight: '1.3',
                            minWidth: 0,
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {getUserDisplayInfo().value || (
                            <span style={{ color: '#bbb' }}>Loading...</span>
                          )}
                        </div>
                      </Col>

                      <Col span={12} style={{ paddingLeft: '8px', minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#b0c4de', marginBottom: '4px' }}>
                          Vehicle
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            lineHeight: '1.3',
                            minWidth: 0,
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {reservationInfo?.reservationData?.vehicleData
                            ? `${reservationInfo.reservationData.vehicleData.vehiclebrand} ${reservationInfo.reservationData.vehicleData.vehiclemodel}`
                            : 'Loading vehicle information...'}
                        </div>
                      </Col>
                    </>
                  )}

                  {reservationInfo?.tripOrderId?.trip_order_id && (
                    <Row
                      style={{
                        color: '#fff',
                        fontSize: 14,
                        width: '100%',
                        marginTop: matches ? 0 : '1rem',
                      }}
                    >
                      <Col span={12}>
                        <div
                          style={{
                            fontSize: matches ? 16 : 12,
                            color: '#b0c4de',
                            marginBottom: '4px',
                          }}
                        >
                          Trip Order
                        </div>
                        <div style={{ fontSize: matches ? 16 : 12, lineHeight: '1.3' }}>
                          {reservationInfo?.tripOrderId?.trip_order_id
                            ? reservationInfo.tripOrderId.trip_order_id
                            : 'Loading trip order information...'}
                        </div>
                      </Col>
                    </Row>
                  )}
                </Row>
              )}

              {/* Time and Location Info */}
              <Row
                justify="space-around"
                style={{
                  color: '#fff',
                  fontSize: matches ? 16 : 14,
                  width: '100%',
                  margin: matches ? '1.5rem 0' : '1rem 0',
                  padding: matches ? '1rem 0' : '0.5rem 0',
                  borderTop: matches ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  borderBottom: matches ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  marginTop: '1rem',
                  backgroundColor: 'rgba(0, 55, 134, 1)',
                  zIndex: 5,
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Col xs={10} md={5}>
                  <div>Start time</div>
                  <div>
                    {reservationInfo?.reservationData?.initstarttime
                      ? convertTime(
                          typeof reservationInfo.reservationData.initstarttime === 'string'
                            ? reservationInfo.reservationData.initstarttime
                            : reservationInfo.reservationData.initstarttime.toString(),
                        )
                      : 'Loading...'}
                  </div>

                  {/* Mobile: Show Name and Vehicle Model here */}
                </Col>
                <Col xs={10} md={5} style={{ marginBottom: matches ? 0 : '1rem' }}>
                  <div>Pick up location</div>
                  <div>
                    {reservationInfo?.stationDetail
                      ? reservationInfo.stationDetail.name || 'N/A'
                      : 'Loading...'}
                  </div>
                  {reservationInfo?.stationDetail?.address && (
                    <div style={{ fontSize: matches ? 13 : 12, color: '#b0c4de' }}>
                      {reservationInfo.stationDetail.address}
                    </div>
                  )}
                </Col>

                {matches && (
                  <Col md={2}>
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        fill="none"
                      >
                        <path
                          d="M18.62 13.42C18.4346 13.6051 18.2875 13.8248 18.1872 14.0668C18.0868 14.3087 18.0352 14.5681 18.0352 14.83C18.0352 15.092 18.0868 15.3513 18.1872 15.5933C18.2875 15.8352 18.4346 16.055 18.62 16.24L26.38 24L18.62 31.76C18.2461 32.134 18.036 32.6412 18.036 33.17C18.036 33.6989 18.2461 34.2061 18.62 34.58C18.994 34.954 19.5012 35.1641 20.03 35.1641C20.5589 35.1641 21.0661 34.954 21.44 34.58L30.62 25.4C30.8055 25.215 30.9525 24.9952 31.0529 24.7533C31.1533 24.5113 31.2049 24.252 31.2049 23.99C31.2049 23.7281 31.1533 23.4687 31.0529 23.2268C30.9525 22.9848 30.8055 22.7651 30.62 22.58L21.44 13.4C20.68 12.64 19.4 12.64 18.62 13.42Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </Col>
                )}

                <Col xs={10} md={5}>
                  <div>End time</div>
                  <div>
                    {reservationInfo?.reservationData?.initstoptime
                      ? convertTime(
                          typeof reservationInfo.reservationData.initstoptime === 'string'
                            ? reservationInfo.reservationData.initstoptime
                            : reservationInfo.reservationData.initstoptime.toString(),
                        )
                      : 'Loading...'}
                  </div>
                </Col>
                <Col xs={10} md={4}>
                  <div>Return location</div>
                  <div>
                    {reservationInfo?.stationDetail
                      ? reservationInfo.stationDetail.name || 'N/A'
                      : 'Loading...'}
                  </div>
                  {reservationInfo?.stationDetail?.address && (
                    <div style={{ fontSize: matches ? 13 : 12, color: '#b0c4de' }}>
                      {reservationInfo.stationDetail.address}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* QR Code Button */}
            <Link to={`/reservation-my-QR?token=${token}`}>
              <Button size="large" icon={<QrcodeOutlined />} style={{ borderRadius: 5 }}>
                My QR
              </Button>
            </Link>

            {/* Passport Section */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '90%',
                margin: '1rem 0 1rem 0',
                padding: matches ? '1rem 0' : '0',
                borderBottom: matches ? '1px solid rgba(255,255,255,0.1)' : 'none',
              }}
            >
              <img src={passportLogo} alt="passport_logo" />
              <Text
                style={{
                  fontSize: matches ? '24px' : '18px',
                  color: '#ffffff',
                  marginLeft: '1rem',
                }}
              >
                Passport
              </Text>
            </div>

            {/* Passport Document - Individual replacement */}
            {documentImages.cl_front ? (
              // Show existing passport image
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  width: '90%',
                }}
              >
                <Text style={{ color: '#fff', marginBottom: '0.5rem', display: 'block' }}>
                  Your Passport:
                </Text>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={documentImages.cl_front}
                    alt="Passport"
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                    onLoad={() => {
                      // Image loaded successfully
                    }}
                  />
                  <div style={{ color: '#fff', fontSize: '12px', marginTop: '4px' }}>
                    {DOCUMENT_MAPPING.cl_front}
                  </div>
                </div>
              </div>
            ) : (
              // Show passport upload component
              <BookingCheckinUploadItem
                name="Upload a photo of your Passport"
                path="national_id"
                icon={<IdcardOutlined style={styleIcon} />}
                matches={matches}
                userId={reservationInfo?.userData?.userid}
                token={token}
                onUploadSuccess={refreshDocumentImages}
              />
            )}

            {/* Selfie with Passport Document - Individual replacement */}
            {documentImages.cl_rear ? (
              // Show existing selfie with passport image
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  width: '90%',
                }}
              >
                <Text style={{ color: '#fff', marginBottom: '0.5rem', display: 'block' }}>
                  Your Selfie with Passport:
                </Text>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={documentImages.cl_rear}
                    alt="Selfie with Passport"
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                  />
                  <div style={{ color: '#fff', fontSize: '12px', marginTop: '4px' }}>
                    {DOCUMENT_MAPPING.cl_rear}
                  </div>
                </div>
              </div>
            ) : (
              // Show selfie with passport upload component
              <BookingCheckinUploadItem
                name="Upload a selfie with your passport"
                path="national_id_selfie"
                icon={<IdcardOutlined style={styleIcon} />}
                matches={matches}
                userId={reservationInfo?.userData?.userid}
                token={token}
                onUploadSuccess={refreshDocumentImages}
              />
            )}

            {/* Driver License Section */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: matches ? '90%' : '90%',
                margin: matches ? '2rem 0 1.5rem 0' : '1rem 0 1rem 0',
                padding: matches ? '1rem 0' : '0',
                borderBottom: matches ? '1px solid rgba(255,255,255,0.1)' : 'none',
              }}
            >
              <img src={driverCardLogo} alt="driver_logo" />
              <Text
                style={{
                  fontSize: matches ? '1.5rem' : '18px',
                  color: '#ffffff',
                  marginLeft: '1rem',
                  fontWeight: matches ? '600' : 'normal',
                }}
              >
                Driver license
              </Text>
            </div>

            {/* Driver License Document - Individual replacement */}
            {documentImages.dl_front ? (
              // Show existing driver license image
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  width: '90%',
                }}
              >
                <Text style={{ color: '#fff', marginBottom: '0.5rem', display: 'block' }}>
                  Your Driver License:
                </Text>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={documentImages.dl_front}
                    alt="Driver License"
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                  />
                  <div style={{ color: '#fff', fontSize: '12px', marginTop: '4px' }}>
                    {DOCUMENT_MAPPING.dl_front}
                  </div>
                </div>
              </div>
            ) : (
              // Show driver license upload component
              <BookingCheckinUploadItem
                name="Upload a photo of your driver license"
                path="car_license"
                icon={<CarOutlined style={styleIcon} />}
                matches={matches}
                userId={reservationInfo?.userData?.userid}
                token={token}
                onUploadSuccess={refreshDocumentImages}
              />
            )}

            {/* Selfie with Driver License Document - Individual replacement */}
            {documentImages.dl_rear ? (
              // Show existing selfie with driver license image
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  width: '90%',
                }}
              >
                <Text style={{ color: '#fff', marginBottom: '0.5rem', display: 'block' }}>
                  Your Selfie with Driver License:
                </Text>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={documentImages.dl_rear}
                    alt="Selfie with Driver License"
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                  />
                  <div style={{ color: '#fff', fontSize: '12px', marginTop: '4px' }}>
                    {DOCUMENT_MAPPING.dl_rear}
                  </div>
                </div>
              </div>
            ) : (
              // Show selfie with driver license upload component
              <BookingCheckinUploadItem
                name="Upload a selfie with your driver license"
                path="car_license_selfie"
                icon={<CarOutlined style={styleIcon} />}
                matches={matches}
                userId={reservationInfo?.userData?.userid}
                token={token}
                onUploadSuccess={refreshDocumentImages}
              />
            )}
          </div>

          {/* Payment Receipt for Successful Deposit */}
          {chargeSuccess &&
            ((reservationInfo?.reservationData?.overide_deposit !== -1
              ? reservationInfo?.reservationData?.overide_deposit
              : reservationInfo?.reservationData?.depositprice_b) || 0) > 0 && (
              <PaymentReceiptComponent
                reservationInfo={reservationInfo}
                matches={matches}
                onDownloadReceipt={downloadReceipt}
              />
            )}

          {/* Deposit Notice or Deposit Free Badge */}
          {isDepositFree() ? (
            /* Deposit Free Badge */
            <div
              style={{
                width: matches ? '100%' : '100%',
                marginTop: matches ? '1rem' : '0.5rem',
                marginBottom: '1rem',
                backgroundColor: '#f6ffed',
                borderRadius: matches ? '12px' : '8px',
                border: '2px solid #52c41a',
                padding: matches ? '20px 24px' : '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                boxShadow: matches ? '0 4px 12px rgba(82, 196, 26, 0.15)' : 'none',
              }}
            >
              {/* Success Icon */}
              <div
                style={{
                  width: matches ? '48px' : '40px',
                  height: matches ? '48px' : '40px',
                  backgroundColor: '#52c41a',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg
                  width={matches ? '24' : '20'}
                  height={matches ? '24' : '20'}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Badge Content */}
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: matches ? '24px' : '20px',
                    fontWeight: '700',
                    color: '#52c41a',
                    marginBottom: '8px',
                    letterSpacing: '0.5px',
                  }}
                >
                  DEPOSIT FREE
                </div>
                <div
                  style={{
                    fontSize: matches ? '16px' : '14px',
                    color: '#389e0d',
                    lineHeight: '1.4',
                    fontWeight: '500',
                  }}
                >
                  No deposit required for this reservation. You can proceed directly to vehicle
                  pickup!
                </div>
              </div>
            </div>
          ) : !chargeSuccess ? (
            /* Regular Deposit Notice */
            <div
              style={{
                width: matches ? '100%' : '100%',
                marginTop: matches ? '1rem' : '0.5rem',
                marginBottom: '1rem',
                backgroundColor: '#e8f4fd',
                borderRadius: matches ? '12px' : '8px',
                border: '1px solid #91caff',
                padding: matches ? '16px 20px' : '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: matches ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              {/* Info Icon */}
              <div
                style={{
                  width: matches ? '32px' : '28px',
                  height: matches ? '32px' : '28px',
                  backgroundColor: '#1890ff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg
                  width={matches ? '18' : '16'}
                  height={matches ? '18' : '16'}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Notice Text */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: matches ? '14px' : '13px',
                    fontWeight: '600',
                    color: '#1890ff',
                    marginBottom: '2px',
                  }}
                >
                  💡 Notice
                </div>
                <div
                  style={{
                    fontSize: matches ? '16px' : '14px',
                    color: '#595959',
                    lineHeight: '1.4',
                  }}
                >
                  You can pay the deposit{' '}
                  <span style={{ fontWeight: '600', color: '#1890ff' }}>without any fees</span> at{' '}
                  the counter when you collect your car. Online payment may include processing fees
                  ( 3% of deposit ).
                </div>
              </div>
            </div>
          ) : null}

          {/* Payment Section - Only show if not deposit free and not charged */}
          {!isDepositFree() && !chargeSuccess && (
            <>
              {/* Payment Options */}
              <div
                style={{
                  width: matches ? '100%' : '100%',
                  minHeight: '180px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: '0',
                  backgroundColor: '#fff',
                  borderBottomLeftRadius: matches ? '12px' : '40px',
                  borderBottomRightRadius: matches ? '12px' : '40px',
                  borderTopLeftRadius: matches ? '12px' : 0,
                  borderTopRightRadius: matches ? '12px' : 0,
                  boxShadow: matches ? '0 4px 16px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                <div
                  style={{
                    fontSize: matches ? 18 : 16,
                    fontWeight: 500,
                    color: 'var(--character-primary-85, rgba(0, 0, 0, 0.85))',
                    height: matches ? 80 : 68,
                    borderBottom: '1px solid #60ACFF',
                    backgroundColor: '#DDF0FF',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderTopLeftRadius: matches ? '12px' : 0,
                    borderTopRightRadius: matches ? '12px' : 0,
                  }}
                >
                  <div style={{ width: '90%', display: 'flex', justifyContent: 'space-between' }}>
                    <div>Deposit payment</div>
                    <div>{`${getChargeableDeposit().toLocaleString()} THB`}</div>
                  </div>
                </div>

                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '1rem',
                  }}
                >
                  <div
                    style={{
                      fontWeight: '400',
                      display: 'flex',
                      width: '90%',
                    }}
                  >
                    <Radio.Group value={radioV} onChange={handleChangeRadio}>
                      <Space direction="vertical">
                        {option.map((e, idx) => (
                          <RadioOption key={idx} {...e} />
                        ))}
                      </Space>
                    </Radio.Group>
                  </div>
                </div>

                {/* Credit/Debit Card Form */}
                {showCardForm && (
                  <div
                    style={{
                      width: '90%',
                      display: 'flex',
                      flexDirection: 'column',
                      margin: '1rem auto',
                      padding: matches ? '20px' : '16px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Form form={cardForm} layout="vertical" style={{ width: '100%' }}>
                      <Row gutter={[16, 0]}>
                        <Col span={24}>
                          <Form.Item
                            name="cardName"
                            label="Card Holder Name"
                            rules={[{ required: true, message: 'Please enter card holder name' }]}
                          >
                            <Input placeholder="Enter card holder name" size="large" />
                          </Form.Item>
                        </Col>

                        <Col span={24}>
                          <Form.Item
                            name="cardNumber"
                            label="Card Number"
                            rules={[
                              { required: true, message: 'Please enter card number' },
                              {
                                pattern: /^[\d\s]{13,19}$/,
                                message: 'Please enter a valid card number',
                              },
                            ]}
                          >
                            <Input
                              placeholder="1234 5678 9012 3456"
                              size="large"
                              maxLength={19}
                              onChange={(e) => {
                                // Format card number with spaces
                                const value = e.target.value
                                  .replace(/\s/g, '')
                                  .replace(/(.{4})/g, '$1 ')
                                  .trim()
                                cardForm.setFieldsValue({ cardNumber: value })
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={12} sm={8}>
                          <Form.Item
                            name="expiryMonth"
                            label="Expiry Month"
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <Select placeholder="MM" size="large">
                              {Array.from({ length: 12 }, (_, i) => (
                                <Select.Option key={i + 1} value={`${i + 1}`}>
                                  {String(i + 1).padStart(2, '0')}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={12} sm={8}>
                          <Form.Item
                            name="expiryYear"
                            label="Expiry Year"
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <Select placeholder="YYYY" size="large">
                              {Array.from({ length: 15 }, (_, i) => {
                                const year = new Date().getFullYear() + i
                                return (
                                  <Select.Option key={year} value={`${year}`}>
                                    {year}
                                  </Select.Option>
                                )
                              })}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="cvv"
                            label="CVV/CVC"
                            rules={[
                              { required: true, message: 'Required' },
                              { pattern: /^\d{3,4}$/, message: 'Invalid CVV' },
                            ]}
                          >
                            <Input placeholder="123" size="large" maxLength={4} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '1rem 0',
                  }}
                >
                  Secured Payment by{' '}
                  <img src="/image/omise.svg" alt="Omise" width={120} height={30} />
                </div>
              </div>

              {/* Payment Confirmation */}
              <div
                style={{
                  width: matches ? '100%' : '100%',
                  marginTop: '1rem',
                  backgroundColor: '#ffffff',
                  height: matches ? 100 : 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: matches ? '12px' : '0px',
                  boxShadow: matches ? '0 4px 16px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                <div
                  style={{
                    width: '90%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontWeight: '400',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: matches ? 15 : 13,
                        fontWeight: 300,
                        color: matches ? '#666' : 'inherit',
                      }}
                    >
                      Deposit payment
                    </div>
                    <div
                      style={{
                        fontSize: matches ? 20 : 16,
                        fontWeight: matches ? 600 : 500,
                        color: matches ? '#333' : 'inherit',
                      }}
                    >{`${getChargeableDeposit().toLocaleString()} THB`}</div>
                  </div>

                  {hasPendingPayment() ? (
                    // Show retry button when there's a pending payment
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Button
                        type="primary"
                        loading={loadingPayment}
                        disabled={waitingForPayment}
                        style={{
                          width: matches ? 300 : 200,
                          height: matches ? 48 : 36,
                          fontSize: matches ? '16px' : '14px',
                          fontWeight: matches ? '600' : 'normal',
                          borderRadius: matches ? '8px' : '4px',
                        }}
                        onClick={handleRetryPayment}
                      >
                        {waitingForPayment ? (
                          <>
                            <Spin size="small" style={{ marginRight: '8px' }} />
                            Waiting for Payment...
                          </>
                        ) : (
                          'Continue Payment'
                        )}
                      </Button>
                      <div
                        style={{
                          fontSize: matches ? '12px' : '10px',
                          color: '#666',
                          textAlign: 'center',
                        }}
                      >
                        {waitingForPayment
                          ? 'Payment link opened. Waiting for payment confirmation.'
                          : 'Payment is processing. Click to reopen payment link.'}
                      </div>
                    </div>
                  ) : (
                    // Show regular confirm button when no pending payment
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Button
                        type="primary"
                        loading={loadingPayment}
                        disabled={waitingForPayment}
                        style={{
                          width: matches ? 300 : 200,
                          height: matches ? 48 : 36,
                          fontSize: matches ? '16px' : '14px',
                          fontWeight: matches ? '600' : 'normal',
                          borderRadius: matches ? '8px' : '4px',
                        }}
                        onClick={handleConfirm}
                      >
                        {waitingForPayment ? (
                          <>
                            <Spin size="small" style={{ marginRight: '8px' }} />
                            Waiting for Payment...
                          </>
                        ) : (
                          'Confirm & Pay'
                        )}
                      </Button>
                      {waitingForPayment && (
                        <div
                          style={{
                            fontSize: matches ? '12px' : '10px',
                            color: '#666',
                            textAlign: 'center',
                          }}
                        >
                          Payment link created. Waiting for payment confirmation.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Payment Processing Modal - Only show if not deposit free */}
      {!isDepositFree() && (
        <Modal
          visible={paymentModalVisible}
          footer={null}
          closable={false}
          centered
          maskClosable={false}
        >
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {paymentStep === 'processing' && (
              <>
                <Spin size="large" />
                <div style={{ marginTop: '16px', fontSize: '16px' }}>Processing Payment...</div>
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  Please wait while we process your payment
                </div>
              </>
            )}

            {paymentStep === 'success' && (
              <>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                  Payment Successful!
                </div>
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  Your reservation has been updated
                </div>
              </>
            )}

            {paymentStep === 'error' && (
              <>
                <div style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }}>❌</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff4d4f' }}>
                  Payment Failed
                </div>
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  {paymentErrorMessage || 'Please try again or contact support'}
                </div>
                <Button
                  type="primary"
                  style={{ marginTop: '16px' }}
                  onClick={() => {
                    setPaymentModalVisible(false)
                    setPaymentStep('processing')
                    setPaymentErrorMessage('')
                  }}
                >
                  Close
                </Button>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Footer */}
      <div
        style={{
          width: '100%',
          marginTop: '2rem',
          backgroundColor: '#1D2530',
          padding: matches ? '2rem 1rem' : '1.5rem 1rem',
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            color: '#fff',
            textAlign: 'center',
          }}
        >
          {/* Contact Information */}
          <div
            style={{
              display: 'flex',
              flexDirection: matches ? 'row' : 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: matches ? '3rem' : '1.5rem',
              marginBottom: '1.5rem',
              paddingTop: '1.5rem',
            }}
          >
            {/* Phone */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div
                style={{
                  backgroundColor: '#60ACFF',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                📞
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: matches ? '12px' : '11px', color: '#B0C4DE' }}>Phone</div>
                <div style={{ fontSize: matches ? '14px' : '13px', fontWeight: '500' }}>
                  +66 2-113-1155
                </div>
              </div>
            </div>

            {/* Email */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div
                style={{
                  backgroundColor: '#60ACFF',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✉️
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: matches ? '12px' : '11px', color: '#B0C4DE' }}>Email</div>
                <div style={{ fontSize: matches ? '14px' : '13px', fontWeight: '500' }}>
                  reservation.group@haupcar.com
                </div>
                <div style={{ fontSize: matches ? '14px' : '13px', fontWeight: '500' }}>
                  ctrip@haupcar.com
                </div>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div
            style={{
              backgroundColor: 'rgba(96, 172, 255, 0.1)',
              borderRadius: '8px',
              padding: matches ? '1rem' : '0.75rem',
              marginBottom: '1.5rem',
              border: '1px solid rgba(96, 172, 255, 0.3)',
            }}
          >
            <div
              style={{
                fontSize: matches ? '16px' : '14px',
                fontWeight: '600',
                marginBottom: '0.5rem',
                color: '#60ACFF',
              }}
            >
              Need Help?
            </div>
            <div
              style={{
                fontSize: matches ? '14px' : '12px',
                color: '#B0C4DE',
                lineHeight: '1.4',
              }}
            >
              Our customer support team is here to assist you with any questions about your
              reservation, payment issues, or vehicle pickup. Don't hesitate to contact us!
            </div>
          </div>

          {/* Social Media & Links */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1rem',
            }}
          >
            <div
              style={{
                fontSize: matches ? '14px' : '12px',
                color: '#60ACFF',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => window.open('https://www.haupcar.com/termsandconditions', '_blank')}
            >
              Terms & Conditions
            </div>
            <div style={{ color: '#B0C4DE' }}>|</div>
            <div
              style={{
                fontSize: matches ? '14px' : '12px',
                color: '#60ACFF',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => window.open('https://www.haupcar.com/policypdpa', '_blank')}
            >
              Privacy Policy
            </div>
          </div>

          {/* Copyright */}
          <div
            style={{
              fontSize: matches ? '12px' : '11px',
              color: '#B0C4DE',
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '1rem',
            }}
          >
            © 2025 HaupCar. All rights reserved
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingCheckin
